import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, {
  FadeInDown,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { BragCard, BRAG_CARD_WIDTH } from '@/components/share/BragCard';
import { AppText } from '@/components/ui/AppText';
import {
  BellIcon,
  BookIcon,
  ChevronIcon,
  GearIcon,
  GlobeIcon,
  InfoIcon,
  PersonIcon,
  ShieldIcon,
  StarIcon,
  TrophyIcon,
  WidgetIcon,
  type IconProps,
} from '@/components/ui/icons';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Screen } from '@/components/ui/Screen';
import { VerifiedBadge } from '@/components/ui/VerifiedBadge';
import { isSupabaseConfigured } from '@/config';
import { getFontFamily, useTheme, useThemePalette, type ThemeScheme } from '@/config/theme';
import { useTeam } from '@/features/leagues/hooks';
import { useMyLevel } from '@/features/games/levels';
import { useWallet } from '@/features/games/rewards';
import { useMyPredictionStats } from '@/features/predictions/game';
import { AvatarFrame } from '@/components/profile/AvatarFrame';
import { BadgesShowcase } from '@/components/profile/BadgesShowcase';
import { LevelBar } from '@/components/profile/LevelBar';
import { useEquippedFrame } from '@/features/games/store';
import { useDeleteAccount, useProfile, useUpdateProfile } from '@/features/profile/hooks';
import { formatNumber } from '@/lib/i18n/format';
import { useAuth } from '@/lib/auth/AuthProvider';
import { signOut } from '@/lib/auth/actions';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { captureAndShare } from '@/lib/share/captureAndShare';
import type { Locale } from '@/lib/i18n';

const APP_LINK = 'https://play.google.com/store/apps/details?id=com.tashkeela.app';

function SectionLabel({ label }: { label: string }) {
  return (
    <View className="mb-2.5 mt-1 flex-row items-center gap-2 px-4">
      <View className="h-3.5 w-1 rounded-full bg-volt" />
      <AppText variant="caption" weight="bold" className="uppercase tracking-wider text-soft">
        {label}
      </AppText>
    </View>
  );
}

function SettingRow({
  icon: Icon,
  label,
  children,
  last,
}: {
  icon: React.ComponentType<IconProps>;
  label: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  const palette = useThemePalette();
  return (
    <View
      className={`flex-row items-center gap-3 px-4 py-3.5 ${last ? '' : 'border-b border-line'}`}
    >
      <View className="h-8 w-8 items-center justify-center rounded-full bg-volt-glass">
        <Icon size={16} color={palette.voltBright} />
      </View>
      <AppText variant="caption" weight="medium" className="flex-1 text-ink">
        {label}
      </AppText>
      {children}
    </View>
  );
}

function LinkRow({
  icon: Icon,
  label,
  value,
  onPress,
  last,
}: {
  icon: React.ComponentType<IconProps>;
  label: string;
  value?: string;
  onPress: () => void;
  last?: boolean;
}) {
  const palette = useThemePalette();
  const { locale } = useLocale();
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      className={`flex-row items-center gap-3 px-4 py-3.5 active:bg-surface-2/60 ${
        last ? '' : 'border-b border-line'
      }`}
    >
      <View className="h-8 w-8 items-center justify-center rounded-full bg-volt-glass">
        <Icon size={16} color={palette.voltBright} />
      </View>
      <AppText variant="caption" weight="medium" className="flex-1 text-ink">
        {label}
      </AppText>
      {value ? (
        <AppText variant="caption" className="text-volt-bright" numberOfLines={1}>
          {value}
        </AppText>
      ) : null}
      <View style={{ transform: [{ scaleX: locale === 'ar' ? -1 : 1 }] }}>
        <ChevronIcon size={16} color={palette.muted} />
      </View>
    </Pressable>
  );
}

/**
 * Pill switch with equal-width options. Each option can force its own script
 * font (the Arabic label must render in IBM Plex Arabic even when the app
 * locale is English, otherwise the glyphs fall back to an ugly system font).
 */
function Choice<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string; arabic?: boolean }[];
  value: T;
  onChange: (value: T) => void;
}) {
  const palette = useThemePalette();
  return (
    <View className="flex-row rounded-full border border-line bg-inset p-1">
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Pressable
            key={option.value}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            onPress={() => onChange(option.value)}
            className={`min-w-[64px] items-center justify-center rounded-full px-4 py-2 ${
              selected ? 'bg-volt' : 'active:bg-surface-2'
            }`}
          >
            <Text
              style={{
                fontFamily: getFontFamily(option.arabic ? 'ar' : 'en', 'bold'),
                fontSize: 12.5,
                lineHeight: 17,
                color: selected ? palette.onVolt : palette.muted,
              }}
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

/**
 * Premium avatar: a magenta→cyan gradient ring with a slow "breathing" glow and
 * a soft 3D-ish entrance. Falls back to the display-name initial. Tapping it
 * opens the edit flow (pencil overlay).
 */
function AvatarGlow({
  uri,
  initial,
  onPress,
}: {
  uri: string | null | undefined;
  initial: string;
  onPress?: () => void;
}) {
  const palette = useThemePalette();
  const reducedMotion = useReducedMotion();
  const glow = useSharedValue(reducedMotion ? 0.5 : 0);

  useEffect(() => {
    if (reducedMotion) return;
    glow.value = withRepeat(
      withSequence(withTiming(1, { duration: 2200 }), withTiming(0, { duration: 2200 })),
      -1,
      false,
    );
  }, [reducedMotion, glow]);

  const glowStyle = useAnimatedStyle(() => ({
    opacity: 0.35 + glow.value * 0.5,
    transform: [{ scale: 1 + glow.value * 0.06 }],
  }));

  return (
    <Pressable accessibilityRole="button" onPress={onPress} className="active:opacity-90">
      <View className="items-center justify-center" style={{ width: 92, height: 92 }}>
        {/* Breathing glow halo */}
        <Animated.View
          style={[
            { position: 'absolute', width: 92, height: 92, borderRadius: 46, backgroundColor: palette.brandCyan },
            glowStyle,
          ]}
        />
        {/* Gradient ring */}
        <LinearGradient
          colors={[palette.brandMagenta, palette.brandViolet, palette.brandCyan]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ width: 84, height: 84, borderRadius: 42, alignItems: 'center', justifyContent: 'center' }}
        >
          <View
            className="items-center justify-center overflow-hidden rounded-full bg-surface"
            style={{ width: 76, height: 76 }}
          >
            {uri ? (
              <Image
                source={{ uri }}
                style={{ width: 76, height: 76, borderRadius: 38 }}
                contentFit="cover"
                cachePolicy="memory-disk"
                accessibilityLabel={initial}
              />
            ) : (
              <AppText variant="display" weight="bold" style={{ color: palette.voltBright }}>
                {initial}
              </AppText>
            )}
          </View>
        </LinearGradient>
        {/* Verified seal (sits on a small surface disc so the scallops read
            cleanly against the avatar). The avatar itself stays tap-to-edit. */}
        <View
          className="absolute bottom-0 end-0 items-center justify-center rounded-full bg-surface"
          style={{ width: 28, height: 28 }}
        >
          <VerifiedBadge size={24} />
        </View>
      </View>
    </Pressable>
  );
}

function StatCell({ value, label, divider }: { value: string; label: string; divider?: boolean }) {
  return (
    <View className={`flex-1 items-center py-3 ${divider ? 'border-s border-line' : ''}`}>
      <AppText variant="heading" weight="bold" tabular className="text-volt-bright">
        {value}
      </AppText>
      <AppText variant="micro" weight="medium" className="uppercase tracking-wide text-muted">
        {label}
      </AppText>
    </View>
  );
}

export default function ProfileScreen() {
  const palette = useThemePalette();
  const { scheme, setScheme } = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { session, userId } = useAuth();
  const { locale, setLocale, numeralOptions } = useLocale();
  const profileQuery = useProfile();
  const equippedFrame = useEquippedFrame();
  const statsQuery = useMyPredictionStats();
  const walletQuery = useWallet();
  const levelQuery = useMyLevel();
  const favoriteTeamQuery = useTeam(profileQuery.data?.favorite_team_id ?? undefined);
  const updateProfile = useUpdateProfile();
  const deleteAccount = useDeleteAccount();
  const reducedMotion = useReducedMotion();
  const [restartHint, setRestartHint] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
    };
  }, []);

  const changeLocale = async (next: Locale) => {
    const needsRestart = await setLocale(next);
    setRestartHint(needsRestart);
    if (userId) {
      updateProfile.mutate({ locale: next });
    }
  };

  const onDeletePress = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      confirmTimer.current = setTimeout(() => setConfirmingDelete(false), 5000);
      return;
    }
    deleteAccount.mutate(undefined, {
      onSuccess: () => {
        signOut().catch(() => {
          // Session is already invalid after deletion.
        });
      },
    });
  };

  const displayName = profileQuery.data?.display_name ?? session?.user.email ?? '';

  // "Brag card": rasterise the off-screen branded card with the user's rank /
  // streak (or fall back to rich text). Mirrors the score-predict share flow.
  const bragRef = useRef<View>(null);
  const [bragging, setBragging] = useState(false);
  const stats = statsQuery.data;
  const streak = walletQuery.data?.streak ?? 0;
  const bragData = stats
    ? {
        name: displayName || t('brag.aPlayer'),
        avatarUrl: profileQuery.data?.avatar_url,
        rank: stats.rank,
        points: stats.total_points,
        correct: stats.correct,
        streak,
      }
    : null;

  const onBrag = async () => {
    if (!bragData) return;
    setBragging(true);
    try {
      const n = (x: number) => formatNumber(x, numeralOptions);
      const headline =
        bragData.streak >= 2
          ? t('brag.shareLineStreak', {
              rank: n(bragData.rank),
              streak: n(bragData.streak),
            })
          : t('brag.shareLineRank', {
              rank: n(bragData.rank),
              points: n(bragData.points),
            });
      const message = `${headline}\n${t('common.viaApp')} ${APP_LINK}`;
      await captureAndShare({ viewRef: bragRef, message, dialogTitle: t('brag.kicker') });
    } finally {
      setBragging(false);
    }
  };

  return (
    <Screen>
      <View className="px-4 pb-3 pt-2">
        <AppText variant="title">{t('profile.title')}</AppText>
      </View>
      <ScrollView contentContainerStyle={{ paddingBottom: 32 }}>
        {!isSupabaseConfigured ? (
          <EmptyState
            icon="settings-outline"
            title={t('common.notConfiguredTitle')}
            body={t('common.notConfiguredBody')}
          />
        ) : session ? (
          <Animated.View
            entering={reducedMotion ? undefined : FadeInDown.springify().damping(16)}
            className="mx-4 mb-5 overflow-hidden rounded-card border border-glass-border"
          >
            <View className="absolute inset-0 bg-glass" />
            {/* Brand gradient wash: violet → cyan glow behind the hero */}
            <LinearGradient
              colors={['rgba(138,43,226,0.22)', 'rgba(34,211,255,0.08)', 'rgba(255,255,255,0)']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
            />
            <View className="flex-row items-center gap-4 px-4 py-5">
              <AvatarFrame frameKey={equippedFrame.data} size={92}>
                <AvatarGlow
                  uri={profileQuery.data?.avatar_url}
                  initial={displayName.charAt(0).toUpperCase()}
                  onPress={() => router.push('/profile/edit')}
                />
              </AvatarFrame>
              <View className="flex-1 gap-1">
                <AppText variant="heading" weight="bold" numberOfLines={1}>
                  {profileQuery.data?.display_name ?? '-'}
                </AppText>
                <AppText variant="micro" className="text-muted" numberOfLines={1}>
                  {session.user.email ?? ''}
                </AppText>
                {favoriteTeamQuery.data ? (
                  <View className="mt-1 flex-row items-center gap-1.5">
                    <TeamCrest
                      uri={favoriteTeamQuery.data.logo_url}
                      name={favoriteTeamQuery.data.name}
                      size="sm"
                    />
                    <AppText variant="micro" weight="medium" className="text-soft" numberOfLines={1}>
                      {favoriteTeamQuery.data.name}
                    </AppText>
                  </View>
                ) : (
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => router.push('/profile/edit')}
                    className="mt-1 flex-row items-center gap-1 active:opacity-70"
                  >
                    <Ionicons name="create-outline" size={13} color={palette.voltBright} />
                    <AppText variant="micro" weight="bold" className="text-volt-bright">
                      {t('profile.editProfile')}
                    </AppText>
                  </Pressable>
                )}
              </View>
            </View>
            {levelQuery.data ? (
              <View className="border-t border-glass-border px-4 py-3">
                <LevelBar level={levelQuery.data} />
              </View>
            ) : null}
            {statsQuery.data ? (
              <>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => router.push('/predictions/leaderboard')}
                  className="flex-row border-t border-glass-border active:bg-surface-2/40"
                >
                  <StatCell
                    value={formatNumber(statsQuery.data.total_points, numeralOptions)}
                    label={t('profile.statPoints')}
                  />
                  <StatCell
                    value={formatNumber(statsQuery.data.correct, numeralOptions)}
                    label={t('profile.statCorrect')}
                    divider
                  />
                  <StatCell
                    value={`#${formatNumber(statsQuery.data.rank, numeralOptions)}`}
                    label={t('profile.statRank')}
                    divider
                  />
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t('brag.action')}
                  disabled={bragging}
                  onPress={onBrag}
                  className="flex-row items-center justify-center gap-1.5 border-t border-glass-border bg-volt-glass py-2.5 active:bg-volt-glass-strong disabled:opacity-50"
                >
                  <Ionicons
                    name={bragging ? 'hourglass-outline' : 'share-social'}
                    size={14}
                    color={palette.voltBright}
                  />
                  <AppText variant="caption" weight="bold" className="text-volt-bright">
                    {t('brag.action')}
                  </AppText>
                </Pressable>
              </>
            ) : null}
          </Animated.View>
        ) : (
          <Card className="mx-4 mb-5 gap-3 px-4 py-5">
            <AppText variant="heading">{t('profile.guestTitle')}</AppText>
            <AppText variant="caption" className="leading-5">
              {t('profile.guestBody')}
            </AppText>
            <View className="mt-1 flex-row gap-3">
              <Button label={t('auth.signIn')} compact onPress={() => router.push('/auth/sign-in')} />
              <Button
                label={t('auth.signUp')}
                variant="secondary"
                compact
                onPress={() => router.push('/auth/sign-up')}
              />
            </View>
          </Card>
        )}

        {/* Off-screen branded brag card we capture to a PNG. Absolutely
            positioned far off-canvas (and OUTSIDE the clipping hero card) so it
            never shows; `pointerEvents="none"` keeps it inert. */}
        {bragData ? (
          <View
            style={{ position: 'absolute', left: -9999, top: 0, width: BRAG_CARD_WIDTH }}
            pointerEvents="none"
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
          >
            <BragCard ref={bragRef} data={bragData} />
          </View>
        ) : null}

        <SectionLabel label={t('profile.preferences')} />
        <Card className="mx-4 mb-5 overflow-hidden">
          <SettingRow icon={GlobeIcon} label={t('profile.language')}>
            <Choice<Locale>
              options={[
                { value: 'en', label: 'EN' },
                { value: 'ar', label: 'العربية', arabic: true },
              ]}
              value={locale}
              onChange={(next) => {
                changeLocale(next).catch(() => {
                  // Locale change failures are non-fatal; UI stays as-is.
                });
              }}
            />
          </SettingRow>
          <SettingRow icon={GearIcon} label={t('profile.theme')}>
            <Choice<ThemeScheme>
              options={[
                { value: 'dark', label: t('profile.themeDark'), arabic: locale === 'ar' },
                { value: 'light', label: t('profile.themeLight'), arabic: locale === 'ar' },
              ]}
              value={scheme}
              onChange={setScheme}
            />
          </SettingRow>
          {session ? (
            <>
              <LinkRow
                icon={TrophyIcon}
                label={t('profile.rewards')}
                value={`${formatNumber(walletQuery.data?.coins ?? 0, numeralOptions)} ${t('rewards.coins')}`}
                onPress={() => router.push('/rewards' as never)}
              />
              <LinkRow
                icon={PersonIcon}
                label={t('profile.referral')}
                onPress={() => router.push('/referral' as never)}
              />
              <LinkRow
                icon={StarIcon}
                label={t('profile.favoriteTeam')}
                value={favoriteTeamQuery.data?.name ?? t('profile.noFavoriteTeam')}
                onPress={() => {
                  if (favoriteTeamQuery.data) {
                    router.push(`/team/${favoriteTeamQuery.data.id}`);
                  } else {
                    router.push('/leagues');
                  }
                }}
              />
              <LinkRow
                icon={BellIcon}
                label={t('profile.notifications')}
                onPress={() => router.push('/notifications')}
              />
              <LinkRow
                icon={WidgetIcon}
                label={t('profile.widgets')}
                onPress={() => router.push('/widgets' as never)}
                last
              />
            </>
          ) : null}
          {restartHint ? (
            <View className="border-t border-line bg-volt/10 px-4 py-3">
              <AppText variant="micro" className="text-volt-bright">
                {t('profile.restartForLanguage')}
              </AppText>
            </View>
          ) : null}
        </Card>

        {session ? <BadgesShowcase /> : null}

        <SectionLabel label={t('profile.game')} />
        <Card className="mx-4 mb-5 overflow-hidden">
          <LinkRow
            icon={BookIcon}
            label={t('profile.howToPlay')}
            onPress={() => router.push('/how-to-play')}
          />
          <LinkRow
            icon={InfoIcon}
            label={t('profile.credits')}
            onPress={() => router.push('/credits')}
          />
          <LinkRow
            icon={ShieldIcon}
            label={t('privacy.title')}
            onPress={() => router.push('/privacy')}
            last
          />
        </Card>

        {session ? (
          <>
            <SectionLabel label={t('profile.account')} />
            <View className="gap-3 px-4">
              <Button
                label={t('auth.signOut')}
                variant="secondary"
                onPress={() => {
                  signOut().catch(() => {
                    // Errors here mean the session was already gone.
                  });
                }}
              />
              <Button
                label={
                  confirmingDelete
                    ? t('profile.deleteAccountConfirmTitle')
                    : t('profile.deleteAccount')
                }
                variant="danger"
                loading={deleteAccount.isPending}
                onPress={onDeletePress}
              />
              {confirmingDelete ? (
                <AppText variant="micro" className="text-center text-loss">
                  {t('profile.deleteAccountConfirmBody')}
                </AppText>
              ) : null}
            </View>
          </>
        ) : null}
      </ScrollView>
    </Screen>
  );
}
